import { Authorization_V1, UserManagement_V1 } from "@rw1/auto-generated-clients";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Unauthorized } from "../../components";
import { DataTable } from "../../components/data-table/DataTable";
import { PageLayout } from "../../components/PageLayout";
import { useCurrentUser } from "../../hooks";
import { useAllRoles } from "../../hooks/useAllRoles";
import { useCheckAreaAccess } from "../../hooks/useCheckAreaAccess";
import { useCompaniesData } from '../../hooks/useCompaniesData';
import { useCompanyData } from "../../hooks/useCompanyData";
import { useLocalization } from "../../hooks/useLocalization";
import { formatDateYMD } from "../../shared/utils/date";
import { Routes } from "../../types/NavigationPaths";
import { AddNewUserDialog } from "./AddNewUser/AddNewUserDialog";
import { AddNewUserSuccessDialog } from "./AddNewUser/AddNewUserSuccessDialog";
import { DeleteInvitationDialog } from "./DeleteInvitationDialog";
import { useAllUserRolesForOrganization } from "./hooks/useOrganizationUserRoles";
import { usePendingInvitations } from "./hooks/usePendinginvitations";
import { useUsers } from "./hooks/useUsers";

interface UserRow {
  id: string;
  userId: string;
  name: string;
  emailAddress: string;
  role: string;
  roleNames: string[];
  lastLogin: string;
  lastLoginRaw: Date | undefined;
  type: "invitation" | "user";
}

export const formatRole = (
  role: UserManagement_V1.UserRole | string,
): string => {
  if (typeof role === "string") {

    if (role === "Company Administrator" || role === "Company Manager") {
      return "Company manager";
    }

    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  }

  switch (role) {
    case UserManagement_V1.UserRole.CompanyAdministrator:
    case UserManagement_V1.UserRole.Administrator:
      return "Company manager";
    default:
      return "User";
  }
};

const formatLastLogin = (date: Date | undefined): string => {
  if (!date || date.getTime() < new Date("2000-01-01").getTime()) {
    return "Unknown";
  }

  return formatDateYMD(date);
};

const mapUserToRow = (
  user: UserManagement_V1.UserWithCompanyName,
  userRoles: Authorization_V1.UserRole[],
  allRoles: Authorization_V1.Role[],
  companiesData: Record<string, string> | undefined
): UserRow => {
  const assignedUserRoles = userRoles.filter((ur) => ur.userId === user.userId);

  const assignedRoleNames = assignedUserRoles
    .map((ur) => {
      const role = allRoles.find((r) => r.roleId === ur.roleId);
      if (!role) return null;

      const baseRoleName = formatRole(role.roleName);

      const orgId = ur.organizationId;
      if (orgId && companiesData) {
        const companyName = companiesData[orgId];
        if (companyName) {
          return `${baseRoleName} (${companyName})`;
        }
      }

      return baseRoleName;
    })
    .filter((name): name is string => Boolean(name));

  const roleNames = assignedRoleNames.length > 0
    ? Array.from(new Set(assignedRoleNames))
    : ['User'];

  return {
    id: user.userId,
    userId: user.userId,
    name: `${user.surname}, ${user.forename}`,
    emailAddress: user.emailAddress,
    role: roleNames.join(", "),
    roleNames,
    lastLogin: formatLastLogin(user.lastLogin),
    lastLoginRaw: user.lastLogin,
    type: "user",
  };
};

const mapInvitationToRow = (
  invitation: UserManagement_V1.InvitationSummary,
): UserRow => ({
  id: invitation.inviteeEmail ?? "",
  userId: invitation.inviteeEmail ?? "",
  name: "-",
  emailAddress: invitation.inviteeEmail ?? "-",
  role: "Pending Invitation",
  roleNames: ["Pending Invitation"],
  lastLogin: formatDateYMD(invitation.createdAt),
  lastLoginRaw: invitation.createdAt,
  type: "invitation",
});

const INITIAL_SORTING: SortingState = [{ id: "name", desc: false }];

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();

  const { t } = useLocalization();

  const { isLoading: isCurrentUserLoading } = useCurrentUser();
  const { companyId } = useParams();
  const { isLoading: isCheckAccessLoading, access } = useCheckAreaAccess(companyId);
  const { isLoading: isCompaniesDataLoading, companyNamesByIdMap } = useCompaniesData({ enabled: true })
  const areHooksEnabled = !!companyId && !isCheckAccessLoading && access.uam;

  const { users, isLoading: isUsersLoading } = useUsers({ companyId: companyId, enabled: areHooksEnabled });

  const { allRoles, isLoading: isAllRolesLoading } = useAllRoles({ enabled: areHooksEnabled });
  const { userRoles, isLoading: isUserRolesLoading } = useAllUserRolesForOrganization({ organizationId: companyId, enabled: areHooksEnabled })

  const { invitations, isLoading: isPendingInvitationsLoading } = usePendingInvitations({ companyId: companyId, enabled: areHooksEnabled });

  const { isSsoCompany, demoEmailDomain, isLoading: isCompanyDataLoading } = useCompanyData(companyId, areHooksEnabled);

  const combinedData = React.useMemo(
    () => [
      ...users.map((user) => mapUserToRow(user, userRoles, allRoles, companyNamesByIdMap)),
      ...invitations.map(mapInvitationToRow),
    ],
    [users, invitations, userRoles, allRoles, companyNamesByIdMap],
  );

  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isDeleteInvitationDialogOpen, setIsDeleteInvitationDialogOpen] =
    useState(false);
  const [invitationEmailToDelete, setInvitationEmailToDelete] = useState<
    string | null
  >(null);

  const handleRowClick = (userRow: UserRow) => {
    if (userRow.type === "invitation") {
      setInvitationEmailToDelete(userRow.emailAddress);
      setIsDeleteInvitationDialogOpen(true);

      return;
    }

    void navigate(`/companies/${companyId}${Routes.USERS}/${userRow.id}`);
  };

  const handleCloseDeleteInvitationDialog = () => {
    setIsDeleteInvitationDialogOpen(false);
    setInvitationEmailToDelete(null);
  };

  const columns = React.useMemo<ColumnDef<UserRow, any>[]>(
    () => [
      {
        id: "name",
        header: t("users.usersTableHeaders.name"),
        accessorFn: (row) => `${row.name}`,
        cell: (ctx) => (
          <span style={{ fontWeight: 500 }}>{ctx.getValue<string>()}</span>
        ),
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
      },
      {
        accessorKey: "emailAddress",
        header: t("users.usersTableHeaders.email"),
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "text",
      },
      {
        accessorKey: "role",
        header: t("users.usersTableHeaders.role"),
        accessorFn: (row) => row.roleNames,
        cell: (ctx) => ctx.row.original.role,
        enableSorting: false,
        enableColumnFilter: true,
        getUniqueValues: (row: UserRow) => row.roleNames,
        filterFn: "arrIncludesSome",
        size: 80,
      },
      {
        id: "lastLogin",
        header: t("users.usersTableHeaders.lastLogin"),
        accessorFn: (row) => {
          const date = row.lastLoginRaw;
          if (!date || date.getTime() < new Date("2000-01-01").getTime()) {
            return undefined;
          }

          return date.getTime();
        },
        cell: (ctx) => ctx.row.original.lastLogin,
        enableSorting: true,
        enableColumnFilter: false,
        sortUndefined: "last",
        size: 80,
      },
    ],
    [],
  );

  if (!isCheckAccessLoading && !access.uam) {
    return (<Unauthorized />)
  }

  const loading =
    isUsersLoading ||
    isCurrentUserLoading ||
    isPendingInvitationsLoading ||
    isAllRolesLoading ||
    isUserRolesLoading ||
    isCompaniesDataLoading ||
    isCompanyDataLoading;

  return (
    <PageLayout isLoading={loading} maxWidth={false}>
      <DataTable<UserRow>
        actionButtonText={t("users.addUserButton")}
        actionButtonTextShort={t("users.addUserButtonShort")}
        columns={columns}
        data={combinedData}
        defaultSort={INITIAL_SORTING}
        facetedColumnIds={["role"]}
        globalFilterPlaceholder={t("users.usersTableSearchPlaceholder")}
        onActionClick={() => setIsAddUserDialogOpen(true)}
        onRowClick={handleRowClick}
      />
      <AddNewUserDialog
        demoEmailDomain={demoEmailDomain}
        isAddUserDialogOpen={isAddUserDialogOpen}
        isSsoCompany={isSsoCompany}
        setIsAddUserDialogOpen={setIsAddUserDialogOpen}
        setIsSuccessDialogOpen={setIsSuccessDialogOpen}
        setSuccessMessage={setSuccessMessage}
      />
      <AddNewUserSuccessDialog
        isOpen={isSuccessDialogOpen}
        message={successMessage}
        onClose={() => setIsSuccessDialogOpen(false)}
        setMessage={setSuccessMessage}
      />

      <DeleteInvitationDialog
        companyId={companyId}
        invitationEmail={invitationEmailToDelete}
        isOpen={isDeleteInvitationDialogOpen}
        onClose={handleCloseDeleteInvitationDialog}
      />
    </PageLayout>
  );
};